import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Download, KeySquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { errText } from '../../account/ChangePasswordCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Shows freshly issued backup codes once (copy / download), they are never shown again.
export const BackupCodesDialog = ({ codes, onClose }) => {
  const text = codes.join('\n');
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`Authenticator backup codes (each works once)\n\n${text}\n`], { type: 'text/plain' }));
    a.download = 'backup-codes.txt';
    a.click();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="backup-codes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeySquare className="h-5 w-5 text-amber-600" /> Save your backup codes</DialogTitle>
          <DialogDescription>If you lose your phone, each code signs you in once. Store them somewhere safe — they won't be shown again.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-neutral-50 rounded-lg p-4" data-testid="backup-codes-list">
          {codes.map((c) => <span key={c} className="text-center">{c}</span>)}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(text); toast.success('Copied'); }} data-testid="backup-codes-copy"><Copy className="h-4 w-4 mr-2" />Copy</Button>
          <Button variant="outline" onClick={download} data-testid="backup-codes-download"><Download className="h-4 w-4 mr-2" />Download</Button>
          <Button onClick={onClose} data-testid="backup-codes-done">I've saved them</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Remaining backup codes + regenerate (needs password and a current authenticator code).
export const BackupCodesPanel = ({ token, onCodes }) => {
  const [remaining, setRemaining] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ password: '', code: '' });
  const load = () => axios.get(`${API}/api/admin/2fa/status`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setRemaining(r.data.backup_codes_remaining)).catch(() => {});
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = async () => {
    try {
      const { data } = await axios.post(`${API}/api/admin/2fa/backup-codes`, form, { headers: { Authorization: `Bearer ${token}` } });
      setOpen(false); setForm({ password: '', code: '' }); onCodes(data.backup_codes); load();
    } catch (e) { toast.error(errText(e, 'Could not create new codes')); }
  };

  return (
    <div className="mt-4 pt-4 border-t space-y-3" data-testid="backup-codes-panel">
      <p className="text-sm text-neutral-600">Backup codes left: <strong data-testid="backup-codes-remaining">{remaining ?? '…'}</strong>{remaining === 0 && <span className="text-red-600"> — create new ones now</span>}</p>
      {open ? (
        <div className="grid sm:grid-cols-3 gap-2">
          <Input type="password" placeholder="Your password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="regen-codes-password" />
          <Input placeholder="6-digit code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, '').slice(0, 6) })} data-testid="regen-codes-code" />
          <Button onClick={regenerate} disabled={!form.password || form.code.length !== 6} data-testid="regen-codes-submit">Create new codes</Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="regen-codes-btn">Create new backup codes</Button>
      )}
    </div>
  );
};
